import { clsx } from 'clsx';

export const VARIABLE_PREFIX = 'var';
export const FUNCTION_PREFIX = 'func';
export const UNDERSCORE = '_';
export const BLOCK_PREFIX = 'b';
export const BLOCK_ID_PREFIX = `${BLOCK_PREFIX}${UNDERSCORE}`;
export const ENTITY_PREFIX = 'e'; // mongo id prefix for backend entities
export const NAV_ITEM_PREFIX = 'nav';
export const PAGE_EVENT_PREFIX = 'p_evt';
export const GLOBAL_FILTER_ITEM_PREFIX = 'gfItem';
export const PAGE_INPUT_QUERY_KEY = 'pageInputs';
export const EVENT_PREFIX = 'evt';
export const UNSAVED_EVENT = 'unsaved';
export const ACTION_PREFIX = 'act';
export const ROW_ACTION_PREFIX = 'rowAct';
export const BULK_ACTION_PREFIX = 'bulkAct';
export const COLUMN_ACTION_PREFIX = 'colAct';
export const REQUEST_ID_PREFIX = 'req';
export const MULTI_STAT_CARD_SECONDARY_STAT_PREFIX = 'secStat';
export const KEY_VALUE_FIELD_PREFIX = 'kvF';
export const FORM_VALIDATION_RULES_PREFIX = 'f_vRules';
export const STEPPED_CONTAINER_ITEM_PREFIX = 'stepItem';
export const COLUMN_PREFIX = 'col';
export const NAV_MENU_ITEM_PREFIX = 'navMenuItem';
export const NAVIGATION_CONTAINER_ITEM_PREFIX = 'navContainerItem';
export const COMMENT_PREFIX = 'comment';
export const REPLY_PREFIX = 'reply';
export const FIELD_PREFIX = 'field';
export const SECTION_PREFIX = 'section';
export const FIELD_ID_PREFIX = 'F';
export const MEDIA_ID_PREFIX = 'media';
export const MESSAGE_INPUT_ACTION = 'messageInputAct';
export const VOICE_MODE_ACTION = 'voiceModeAct';
export const CASCADER_OPTION_PREFIX = 'cascaderOption';
export const TABLE_TOOLBAR_ACTION_PREFIX = 'ttA';
export const TABLE_VIEW_PREFIX = 'tv';
export const FOLDER_PREFIX = 'fd';
export const MQTT_TOPIC_PREFIX = 'mqttTopic';
export const CUSTOM_EVENT_PREFIX = 'customEvent';
export const WALKTHROUGH_STEP_PREFIX = 'wts';
export const PAGE_SCOPE_KEY_PREFIX = 'pageScopeKey';
export const UNSAVED_EVENT_PREFIX = `${UNSAVED_EVENT}${EVENT_PREFIX}`;
export const MODULE_ID_DELIMITER = '$$';

export const HEADER_ID = 'header_id';
export const ROOT_ID = 'root_id';
export const FOOTER_ID = 'footer_id';

/** id of the single FieldBuilder block added when creating a new field page */
export const FIELD_BUILDER_BLOCK_ID = 'b_field_builder_block';

export const EXPORT_HEADER_ID = 'export_header_id';
export const EXPORT_FOOTER_ID = 'export_footer_id';

// root block id for the shimmer loader tree on a template component page
export const LOADER_ROOT_ID = 'loader_root_id';

export const BLOCK_STYLES = 'block-styles';
export const INTERNALS_PARAMETERS_KEY = '__internals__';
export const CLIENT_KEY = 'm';
export const CLIENT_TYPE_KEY = 'c';
export const PAGE_SLUG_KEY = 's';
export const PLATFORM_KEY = 'p';

export const RUN_TIME_BLOCK_KEY = 'runTimeBlockKey';

//to differentiate between block and placeholder block
export const PLACEHOLDER = 'placeholder';
//for dnd on pill
export const OVERLAY_PILL = 'overlayPill';

export const ENTITY_DATA_SOURCE_PREFIX = 'entityDataSource';
// Action constants
export const ENTITY_DATA_SOURCE_SEPARATOR = '$';

export const LOCATION_ID_PREFIX = 'location';
export const interfacePatterns = {
  subDomain: '^(?!-)(?!.*--)[a-z0-9-]+(?<!-)$',
};

export const NAV_DIRECTION_ATTRIBUTE = 'data-nav-direction';
export const PREVIEW_DATA_SOURCE_QUERY_KEY = 'preview-ds-';

export const GLOBAL_INTERFACE_PAGE_ID_PREFIX = `${ENTITY_PREFIX}_global_`;
export const GLOBAL_INTERFACE_PAGE_SLUG_PREFIX = 'global-page-of-';

// deterministic id for the per-(interface, locale) common i18n namespace, so
// repeat creates collide on one entity instead of minting duplicates. mirrors
// GLOBAL_INTERFACE_PAGE_ID_PREFIX — the server honors client-provided ids.
export const COMMON_NAMESPACE_ID_PREFIX = `${ENTITY_PREFIX}_common_ns_`;

/**
 * Controls whether to disable Suspense boundaries in the GlobalStateStore.
 *
 * Why this is set to false:
 * During navigation between pages in the matrix app using AppSideNav, Next.js performs
 * the following sequence:
 *
 * 1. When navigating between pages (e.g., A → B), Next.js makes a request to fetch
 *    the React Server Component (RSC) for the target page
 *
 * 2. While this RSC request is in flight, Next.js attempts to show loading states in
 *    this order:
 *    a. First tries to find and use loading.tsx in the [interfacePageSlug]/loading.tsx or higher in the layout hierarchy (we don't have this in matrix)
 *    b. If not, then it falls back to the closest Suspense boundary in the page's component hierarchy
 *
 * 3. Without this flag set to true, users would see loading states during navigation
 *    even for cached/previously visited pages due to the RSC fetch
 *
 * To reproduce this behavior:
 * 1. Set this to true
 * 2. Create two pages: one with a table, one with simple text
 * 3. Visit both pages at least once in matrix
 * 4. Enable 6x CPU slowdown and 3G network throttling in Chrome DevTools
 * 5. Navigate between the pages - you'll see unnecessary loading states
 */
export const WITH_SUSPENSE = false;

export const INITIAL_TRANSFORMER_CODE = `// Query response is available as the \`data\` variable\nreturn data;`;
export const DUMMY_INTERFACE_RECORD_ID = 'dummy-id';

/** Used in field/form builder when there is no parent interface; skip interface fetches and mimic a dummy record for the store. */
export const NO_INTERFACE_RECORD_ID = 'no-interface';

export const INTERFACE_BUILDER_PAGE_CONTAINER_ID = 'interface-builder-page-container';

export const ALL_FILE_TYPES = '*/*';
/** Key used to store the user's preferred color scheme (light/dark/system)
 *  Key value is kept as `theme` for backward compatibility
 */
export const COLOR_SCHEME_USER_PREFERENCE_KEY = 'theme';
/** Key used to store the user's preferred theme from the themes added to the interface from theme manager
 *  Key value is kept as `interfaceTheme` to avoid conflict with the `theme` key
 */
export const THEME_USER_PREFERENCE_KEY = 'interfaceTheme';

export const CUSTOM_BLOCK_LATEST_DEPLOYED_VERSION = '-1';
export const CUSTOM_BLOCK_DEVELOPMENT_VERSION = '-2';

export const ARENA_SELECTOR = '[data-name="arena"]';

export const NO_CODE_BUILDER_WALKTHROUGH_ID = 'no-code-builder-walkthrough-id';
export const NAVIGATION_CONTAINER_PREVIEW = 'navigation-container-preview' as const;

export const NOCODE_NAVIGATION_CONTAINER = 'nocode-navigation-container' as const;

export const CUSTOM_NAVIGATION_CONTAINER = 'custom-navigation-container' as const;

export const AI_BUILDING_PAGE_SLUG = 'building-app';

export const FIGMA_BUILDING_PAGE_SLUG = 'building-page';

// v1 authoring allowlist of block component types a consumer can override via
// component-overrides on a TemplateComponent instance. Any child whose componentType is
// not in this set is hidden from the ComponentOverridesField list and blocked at
// the ComponentOverridesDetailsView entry point. Extending this set is
// an intentional product call — coordinate with designer before adding.
export const SUPPORTED_COMPONENT_OVERRIDES_BLOCKS: ReadonlySet<string> = new Set([
  'Typography',
  'Stack',
  'Button',
  'Icon',
  'TextInput',
  'TemplateComponent',
  'IconButton',
  'Tag',
]);

export const PATH_ROOT_ID = 'f_root';
export const DYNAMIC_SEGMENT = '/:';
export const DYNAMIC_PARAM_PREFIX = ':';
export const PATH_NODE_PREFIX = 'f_';
export const BASE_PATH = '/';

export const ADD_ITEM_EMPTY_STATE_CARD_SHADOW = '!shadow-[0_1px_2px_0_var(--palette-gray-200)]';

export const ADD_ITEM_EMPTY_STATE_ROW_CLASSES = clsx(
  ADD_ITEM_EMPTY_STATE_CARD_SHADOW,
  'border-secondary hover:bg-secondary mt-1 cursor-pointer bg-primary rounded-sm border h-7 gap-xs',
);

export enum InterfaceModes {
  BUILDER = 'BUILDER',
  PREVIEW = 'PREVIEW',
  RUNNER = 'RUNNER',
}

// Non-interactive empty / loading state box for InterfaceBuilder list fields: 28px tall (h-7),
// theme-aware white surface (bg-primary), 6px radius (rounded-sm), secondary border, gapped from
// the label above (mt-1). Sibling of ADD_ITEM_EMPTY_STATE_ROW_CLASSES — that one is the clickable
// add-item row (hover + cursor), this one just centers placeholder text / a loader.
export const EMPTY_STATE_BOX_CLASSES = 'bg-primary border border-secondary rounded-sm h-7 mt-1';

// version-tag prefixes used across saved-versions/compare-versions
export const DEPLOYED_VERSION_PREFIX = 'V';
export const SAVED_VERSION_PREFIX = 'S';
